import React from 'react';

/**
 * AiImageBadge
 * ------------------------------------------------------------------
 * Visible disclosure label for AI-generated / AI-manipulated images,
 * required under the EU AI Act, Art. 50(4) (deployer transparency
 * obligation for "deepfake"-type content: images that resemble real
 * places/objects/entities and could otherwise appear authentic).
 *
 * Usage: wrap any <img> that was generated or heavily manipulated by
 * AI in <AiImageFrame>...</AiImageFrame> (see below), or drop this
 * badge directly into an existing `relative` container.
 *
 * Keep the label:
 *  - permanently visible (not hover-only, not hidden behind a click)
 *  - legible against the image (hence the scrim + backdrop blur)
 *  - present at first exposure, not just in a faraway footer note
 */

interface AiImageBadgeProps {
  /** Position within the parent (parent must be `position: relative`) */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Compact = icon + short label, for small product cards */
  compact?: boolean;
}

export const AiImageBadge: React.FC<AiImageBadgeProps> = ({
  position = 'bottom-right',
  compact = false,
}) => {
  const positionClasses: Record<string, string> = {
    'top-left': 'top-3 left-3',
    'top-right': 'top-3 right-3',
    'bottom-left': 'bottom-3 left-3',
    'bottom-right': 'bottom-3 right-3',
  };

  return (
    <span
      className={`absolute ${positionClasses[position]} z-10 inline-flex items-center gap-1.5
        bg-verde-scuro/80 backdrop-blur-sm text-beige
        font-montserrat font-medium tracking-wide
        text-[11px] leading-none
        px-2.5 py-1.5 rounded-full shadow-sm
        pointer-events-none select-none`}
      role="note"
      aria-label="Immagine generata con intelligenza artificiale"
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="shrink-0"
      >
        <path d="M12 3v3M12 18v3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M3 12h3M18 12h3M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
        <circle cx="12" cy="12" r="3.5" />
      </svg>
      {compact ? 'IA' : 'Immagine generata con IA'}
    </span>
  );
};

/**
 * AiImageFrame
 * ------------------------------------------------------------------
 * Convenience wrapper: sets `position: relative` + `overflow-hidden`
 * on the container and renders the badge on top of whatever is
 * passed as children (an <img>, a background-image div, etc.).
 */
interface AiImageFrameProps extends AiImageBadgeProps {
  className?: string;
  children: React.ReactNode;
}

export const AiImageFrame: React.FC<AiImageFrameProps> = ({
  className = '',
  position,
  compact,
  children,
}) => {
  return (
    <div className={`relative ${className}`}>
      {children}
      <AiImageBadge position={position} compact={compact} />
    </div>
  );
};

export default AiImageBadge;
